import { useState, useCallback, useEffect, useReducer, useMemo } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ChartGrid from "../ChartGrid/ChartGrid";
import { validId } from "../../helpers/utils";
import LoadingModal from "../LoadingModal";
import styles from './SurveyResults.module.css';
import ChartGridContext from "../../contexts/ChartGridContext";
import ModalSurveyQuestion from "../ModalSurveyQuestion/ModalSurveyQuestion";
import { Link } from "react-router-dom";

const reducer = (state, action) => {
	switch (action.type) {
		case ACTION.NEW_CHART_DATA:
			return { ...state, questions: [...action.payload], dataLoaded: true, notFound: false };
		case ACTION.TOGGLE_PEERS_ONLY:
			return {
				...state,
				questions: state.questions.map(question =>
					question.surveyQuestionId === action.payload.surveyQuestionId
						? { ...question, peersOnly: action.payload.peersOnly, responses: [...action.payload.responses] }
						: question
				)
			};
		case ACTION.UPDATE_CHART_DATA:
			return {
				...state,
				questions: state.questions.map(question =>
					question.surveyQuestionId === action.payload.surveyQuestionId
						? action.payload
						: question
				)
			};
		case ACTION.NOT_FOUND:
			return { ...state, dataLoaded: true, notFound: true };
		default:
			console.warn(`unhandled action.type: ${action.type}`);
			return state;
	}
}

const ACTION = {
	NEW_CHART_DATA: "newChartData",
	TOGGLE_PEERS_ONLY: "togglePeersOnly",
	UPDATE_CHART_DATA: "updateChartData",
	NOT_FOUND: "notFound"
}

const SurveyResults = () => {

	const { surveyId } = useParams();

	const axiosPrivate = useAxiosPrivate();

	const navigate = useNavigate();
	const location = useLocation();

	const [state, dispatch] = useReducer(reducer, {
		questions: [],
		dataLoaded: false,
		notFound: false
	});

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [selectedSurveyQuestionId, setSelectedSurveyQuestionId] = useState(null);

	useEffect(() => {

		// This effect is triggered the first time the page loads, or if the surveyId changes

		let isMounted = true;

		const controller = new AbortController();

		const getResults = async () => {

			try {
				const response = await axiosPrivate.get(`/surveys/${surveyId}/results`,
					{
						signal: controller.signal
					});
				if (isMounted) {
					setTitle(response.data.title);
					setDescription(response.data.description);
					dispatch({
						type: ACTION.NEW_CHART_DATA, payload: response.data.questions || []
					});
				}
			} catch (err) {
				if (isMounted) {
					console.error(err);
					if (err.response?.status === 404) {
						dispatch({
							type: ACTION.NOT_FOUND
						});
					} else if (err.response?.status === 403) {
						navigate('/login', { state: { from: location }, replace: true });
					}
				}
			}
		}

		if (validId(surveyId)) {
			getResults();
		}

		return () => {
			isMounted = false;
			controller.abort();
		}
	}, [surveyId]) // eslint-disable-line react-hooks/exhaustive-deps

	const togglePeersOnly = useCallback(async (surveyQuestionId) => {
		console.log(`surveyQuestionId: ${surveyQuestionId} `);

		const question = state.questions.find(q => q.surveyQuestionId === surveyQuestionId);
		const request = question?.peersOnly ? axiosPrivate.delete : axiosPrivate.post;

		try {
			const response = await request(`/profile/peersonly/${surveyQuestionId}`);
			dispatch({
				type: ACTION.TOGGLE_PEERS_ONLY, payload: response.data
			});
		} catch (err) {
			console.log(err);
		}

	}, [state.questions]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateChartResults = useCallback(async (surveyQuestionId) => {

		console.log(`updateChartResults ${(new Date().getTime())}`);


		const controller = new AbortController();

		try {
			const response = await axiosPrivate.get(`/surveyQuestions/${surveyQuestionId}/results`,
				{
					signal: controller.signal
				});
			console.log(`new chart results loaded at ${(new Date().getTime())}`);
			dispatch({
				type: ACTION.UPDATE_CHART_DATA,
				payload: response.data
			});
		} catch (err) {
			console.log(err);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps


	const showModalSurveyQuestion = useCallback((surveyQuestionId) => {
		setSelectedSurveyQuestionId(surveyQuestionId);
	}, []);

	const closeModalSurveyQuestionDialog = useCallback(() => {
		setSelectedSurveyQuestionId(null);
	}, []);

	const chartGridContextValue = useMemo(() => ({
		showModalSurveyQuestion,
		togglePeersOnly,
		updateChartResults
	}), [showModalSurveyQuestion, togglePeersOnly, updateChartResults]);

	const fromSurveyId = (location.state?.fromSurveyId);
	let to, toName;
	if (fromSurveyId !== undefined) {
		to = `/insights/${fromSurveyId}`;
		toName = "Back to Survey";
	} else {
		to = "/insights";
		toName = " Back to Insights";
	}

	if (!state.dataLoaded) {
		return (
			<LoadingModal />
		)
	} else if (state.notFound) {
		return (
			<div className={styles.notFound}>
				<h3>Our apologies, but this survey is not available.</h3>
				<Link className={styles.back} to={to}>{toName}</Link>				
			</div>
		)

	} else {

		return (
			<section className={styles.container}>
				<h1 className={styles.title}>{title}</h1>
				{
					description && <h2 className={styles.description}>{description}</h2>
				}
				<ChartGridContext.Provider value={chartGridContextValue}>
					<ChartGrid chartData={state.questions} />
					{selectedSurveyQuestionId && <ModalSurveyQuestion surveyQuestionId={selectedSurveyQuestionId} onCloseDialog={closeModalSurveyQuestionDialog} />}
				</ChartGridContext.Provider>
				<Link className={styles.back} to={to}>{toName}</Link>
			</section>
		);
	}

};

export default SurveyResults;